import { ChangeEvent, FormEvent, useState } from 'react';
import { AppInputProps } from '@/types/common';
import { useTranslation } from 'react-i18next';
import { PASSWORD_REGEX } from '@/utils/common';

const NAME_REGEX = /^[-A-Za-z\s'"–.,֐-׿À-ÿĀ-ǿȀ-ʯ]+$/;

const getNameFieldError = (
  id: string,
  value: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  const isFirstnameField = id === 'firstname';

  if (!NAME_REGEX.test(value)) {
    return isFirstnameField ? t('invalidFirstname') : t('invalidLastname');
  }

  if (isFirstnameField && value.length > 30) {
    return t('settings.add_students.errors.first_name_too_long');
  }

  if (!isFirstnameField && value.length > 40) {
    return t('settings.add_students.errors.last_name_too_long');
  }

  return '';
};

const UsePersonalDetails = () => {
  const { t } = useTranslation();

  const onInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = target;

    setPersonalDetailsFields(prevState => {
      const updatedState = [...prevState];

      const fieldIndex = updatedState.findIndex(field => field.name === name);
      const id = updatedState[fieldIndex].id;

      if (name === 'termsAndConditions') {
        updatedState[fieldIndex].value =
          updatedState[fieldIndex].value === 'false' ? 'true' : 'false';
      } else {
        updatedState[fieldIndex].value = value;
      }

      if (id === 'firstname' || id === 'lastname') {
        const nameError = value ? getNameFieldError(id, value, t) : '';
        updatedState[fieldIndex].isValid = Boolean(value) && !nameError;
        updatedState[fieldIndex].error = nameError;
      } else {
        updatedState[fieldIndex].isValid = false;
        updatedState[fieldIndex].error = '';
      }

      return updatedState;
    });
  };

  const onPersonalDetailsSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setPersonalDetailsFields(prevState => {
      const updatedState = [...prevState];

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      for (let i = 0; i < updatedState.length; i++) {
        const { id, value } = updatedState[i];

        if (!value) continue;

        const isFirstnameField = id === 'firstname';
        const isLastnameField = id === 'lastname';
        const isPasswordField = id === 'password';
        const isConfirmPasswordField = id === 'confirm_password';

        const passwordIndex = updatedState.findIndex(field => field.id === 'password');

        const passwordValue = updatedState[passwordIndex].value;

        if (isFirstnameField || isLastnameField) {
          const nameError = getNameFieldError(id, value, t);

          updatedState[i].error = nameError;
          updatedState[i].isValid = !nameError;
        } else if (id === 'email') {
          const isEmailValid = emailRegex.test(value);
          updatedState[i].isValid = isEmailValid;

          if (!isEmailValid) {
            updatedState[i].error = t('invalidEmail');
          }
        } else if (isPasswordField || isConfirmPasswordField) {
          const isPasswordValid = PASSWORD_REGEX.test(value);

          updatedState[i].isValid = isPasswordValid;

          if (!isPasswordValid) {
            updatedState[i].error = t('invalidPassword');
          } else if (isConfirmPasswordField) {
            const isIdentical = updatedState[passwordIndex].isValid && value === passwordValue;

            if (!isIdentical) {
              updatedState[passwordIndex].isValid = false;
              updatedState[passwordIndex].error = '';

              updatedState[i].isValid = false;
              updatedState[i].error = t('passwordsDontMatch');
            }
          }
        } else if (id === 'terms_and_conditions') {
          const isTermsAndConditionsValid = value === 'true';

          updatedState[i].isValid = isTermsAndConditionsValid;

          if (!isTermsAndConditionsValid) {
            updatedState[i].error = t('fieldIsMandatory');
          }
        }
      }

      return updatedState;
    });
  };

  const onEmailIsAlreadyTaken = () => {
    setPersonalDetailsFields(prevState => {
      const updatedState = [...prevState];

      const emailFieldIndex = updatedState.findIndex(field => field.id === 'email');

      updatedState[emailFieldIndex].isValid = false;
      updatedState[emailFieldIndex].error = ' ';

      return updatedState;
    });
  };

  const initialPersonalDetailsFields: AppInputProps[] = [
    {
      id: 'firstname',
      name: 'firstname',
      label: 'firstname',
      placeholder: 'firstname',
      value: '',
      error: '',
      isRequired: true,
      isValid: false,
      emitChange: onInputChange,
    },
    {
      id: 'lastname',
      name: 'lastname',
      label: 'lastname',
      placeholder: 'lastname',
      value: '',
      error: '',
      isRequired: true,
      isValid: false,
      emitChange: onInputChange,
    },
    {
      id: 'email',
      name: 'email',
      type: 'email',
      label: 'email',
      placeholder: 'emailExample',
      value: '',
      error: '',
      isRequired: true,
      isValid: false,
      emitChange: onInputChange,
    },
    {
      id: 'password',
      name: 'password',
      type: 'password',
      label: 'password',
      placeholder: 'yourPasswordHere',
      value: '',
      error: '',
      isRequired: true,
      isValid: false,
      showPasswordRequirements: true,
      emitChange: onInputChange,
    },
    {
      id: 'confirm_password',
      name: 'confirmPassword',
      type: 'password',
      label: 'confirmPassword',
      placeholder: 'yourPasswordHere',
      value: '',
      error: '',
      isRequired: true,
      isValid: false,
      emitChange: onInputChange,
    },
  ];

  const [personalDetailsFields, setPersonalDetailsFields] = useState<AppInputProps[]>(
    initialPersonalDetailsFields
  );

  const onUpdatePersonalDetailsFields = () => {
    setPersonalDetailsFields(prevState => {
      const termsAndConditionsField = {
        id: 'terms_and_conditions',
        name: 'termsAndConditions',
        type: 'checkbox',
        value: 'false',
        error: '',
        isRequired: true,
        isValid: false,
        emitChange: onInputChange,
      };

      return [...prevState, termsAndConditionsField];
    });
  };

  return {
    personalDetailsFields,
    onEmailIsAlreadyTaken,
    onUpdatePersonalDetailsFields,
    onPersonalDetailsSubmit,
  };
};

export default UsePersonalDetails;
